import _AccountLogOutMutation from "../__generated__/AccountLogOutMutation.graphql";
import _AccountSettingsDeleteAccountMutation from "../__generated__/AccountSettingsDeleteAccountMutation.graphql";
import _AccountSettingsChangePasswordMutation from "../__generated__/AccountSettingsChangePasswordMutation.graphql";
import { useState } from 'react';
import { LogOut as SignOutIcon } from 'lucide-react';
import { graphql, useMutation } from 'react-relay';
import { t } from '../i18n/t';
import { clearAllDrafts } from '../listen/draft.ts';
import { refusalFor } from '../profile/authReason.ts';
import type { AccountLogOutMutation } from '../__generated__/AccountLogOutMutation.graphql';
import type { AccountSettingsChangePasswordMutation } from '../__generated__/AccountSettingsChangePasswordMutation.graphql';
import type { AccountSettingsDeleteAccountMutation } from '../__generated__/AccountSettingsDeleteAccountMutation.graphql';
const ChangePassword = (_AccountSettingsChangePasswordMutation.hash && _AccountSettingsChangePasswordMutation.hash !== "22b3a04ea7642c9115b305bd3c737bd1" && console.error("The definition of 'AccountSettingsChangePasswordMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountSettingsChangePasswordMutation);
const DeleteAccount = (_AccountSettingsDeleteAccountMutation.hash && _AccountSettingsDeleteAccountMutation.hash !== "2c71a2baae1392493f7eea7ff69c100e" && console.error("The definition of 'AccountSettingsDeleteAccountMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountSettingsDeleteAccountMutation);
const LogOut = (_AccountLogOutMutation.hash && _AccountLogOutMutation.hash !== "1fc72efeb606899db3ec265485979500" && console.error("The definition of 'AccountLogOutMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountLogOutMutation);
export function AccountSettings() {
  const [logOut] = useMutation<AccountLogOutMutation>(LogOut);
  const [changePassword, changing] = useMutation<AccountSettingsChangePasswordMutation>(ChangePassword);
  const [deleteAccount, deleting] = useMutation<AccountSettingsDeleteAccountMutation>(DeleteAccount);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [said, setSaid] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteSaid, setDeleteSaid] = useState<string | null>(null);
  const restart = () => window.location.assign('/');
  // Signed out, or gone: the next person on this browser must not find the
  // learner's unsent retellings (listen/draft.ts).
  const leave = () => {
    clearAllDrafts();
    restart();
  };
  return <div className="flex flex-col gap-6">
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-5 sm:p-8">
          <form className="flex flex-col gap-2" onSubmit={e => {
          e.preventDefault();
          setSaid(null);
          changePassword({
            variables: {
              current,
              next
            },
            onCompleted: res => {
              setSaid(res.changePassword.ok ? t('settings-password-changed') : t(refusalFor(res.changePassword.reason).message));
              if (res.changePassword.ok) {
                setCurrent('');
                setNext('');
              }
            },
            onError: () => setSaid(t('settings-unreachable'))
          });
        }}>
            <h2 className="card-title">{t('settings-change-password')}</h2>
            <label className="fieldset-label mt-2" htmlFor="current-password">
              {t('settings-current-password')}
            </label>
            <input id="current-password" className="input w-full" type="password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} />
            <label className="fieldset-label mt-2" htmlFor="new-password">
              {t('settings-new-password')}
            </label>
            <input id="new-password" className="input w-full" type="password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} />
            <button className="btn btn-primary mt-2 self-start" disabled={changing || !current || !next}>
              {t('settings-change-password')}
            </button>
            {said && <p role="status" className="text-sm">
                {said}
              </p>}
          </form>
        </div>
      </div>

      {/* Its own row, outlined rather than red: signing out loses nothing. */}
      <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2 self-start" onClick={() => {
      logOut({
        variables: {},
        onCompleted: () => leave(),
        onError: () => restart()
      });
    }}>
        <SignOutIcon aria-hidden className="size-4" />
        {t('settings-sign-out')}
      </button>

      <div className="card border border-error/40 bg-base-100 shadow-sm">
        <div className="card-body p-5 sm:p-8 flex flex-col gap-2">
          <h2 className="card-title text-error">{t('settings-delete-title')}</h2>
          <p className="text-sm">{t('settings-delete-body')}</p>
          {!confirming ? <button type="button" className="btn btn-outline btn-error self-start" onClick={() => setConfirming(true)}>
              {t('settings-delete')}
            </button> : <form className="flex flex-col gap-2" onSubmit={e => {
          e.preventDefault();
          setDeleteSaid(null);
          deleteAccount({
            variables: {
              password: deletePassword
            },
            onCompleted: res => {
              if (res.deleteAccount.ok) leave();else setDeleteSaid(t('settings-delete-wrong-password'));
            },
            onError: () => setDeleteSaid(t('settings-unreachable'))
          });
        }}>
              <input className="input w-full" type="password" autoComplete="current-password" placeholder={t('settings-delete-password')} value={deletePassword} onChange={e => setDeletePassword(e.target.value)} />
              <div className="flex gap-2">
                <button className="btn btn-error" disabled={deleting || !deletePassword}>
                  {t('settings-delete-confirm')}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => {
              setConfirming(false);
              setDeletePassword('');
              setDeleteSaid(null);
            }}>
                  {t('common-cancel')}
                </button>
              </div>
              {deleteSaid && <p role="status" className="text-sm text-error">
                  {deleteSaid}
                </p>}
            </form>}
        </div>
      </div>
    </div>;
}